import React, { useState, useEffect } from 'react';
import { zoneService, Zone } from '../../services/zoneService';
import { Button, Table, Modal, Form, Input, InputNumber, message, Popconfirm } from 'antd';

const ZoneManagement: React.FC = () => {
  const [zones, setZones] = useState<Zone[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      setLoading(true);
      const data = await zoneService.getAllZones();
      setZones(data);
    } catch (error) {
      message.error('Erreur lors de la récupération des zones');
    } finally {
      setLoading(false);
    }
  };

  const handleAddZone = () => {
    setEditingZone(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditZone = (zone: Zone) => {
    setEditingZone(zone);
    form.setFieldsValue(zone);
    setIsModalVisible(true);
  };

  const handleDeleteZone = async (id: string) => {
    try {
      await zoneService.deleteZone(id);
      message.success('Zone supprimée avec succès');
      fetchZones();
    } catch (error) {
      message.error('Erreur lors de la suppression de la zone');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingZone) {
        await zoneService.updateZone(editingZone._id, values);
        message.success('Zone mise à jour avec succès');
      } else {
        await zoneService.createZone(values);
        message.success('Zone créée avec succès');
      }
      setIsModalVisible(false);
      fetchZones();
    } catch (error) {
      message.error('Erreur lors de la sauvegarde de la zone');
    }
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Prix de livraison',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `${Number(price).toLocaleString('fr-FR')} FCFA`,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Zone) => (
        <div>
          <Button type="link" onClick={() => handleEditZone(record)}>
            Modifier
          </Button>
          <Popconfirm
            title="Supprimer cette zone ?"
            description="Cette action est définitive."
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteZone(record._id)}
          >
            <Button type="link" danger>
              Supprimer
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Button type="primary" onClick={handleAddZone} style={{ marginBottom: 16 }}>
        Ajouter une zone
      </Button>
      <Table dataSource={zones} columns={columns} rowKey="_id" loading={loading} />

      <Modal
        title={editingZone ? 'Modifier la zone' : 'Ajouter une zone'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        okText={editingZone ? 'Enregistrer' : 'Créer'}
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Nom de la zone"
            rules={[{ required: true, message: 'Veuillez entrer le nom de la zone' }]}
          >
            <Input placeholder="Ex: Plateau, Almadies, Parcelles..." />
          </Form.Item>
          <Form.Item
            name="price"
            label="Prix de livraison (FCFA)"
            rules={[{ required: true, message: 'Veuillez entrer le prix de livraison' }]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder="Ex: 1500"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
            />
          </Form.Item>
          <Form.Item name="description" label="Description (optionnel)">
            <Input.TextArea placeholder="Précisions sur la zone..." rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ZoneManagement;
